import { ArrowRight, Smartphone, Sparkles, Star, Play, Shield, Zap, Users, CheckCircle, MapPin, Camera, Brain, Calendar, BookOpen } from 'lucide-react';
import { motion, useMotionValue, useTransform } from 'motion/react';
import { Button } from '@/components/ui/button';
import { useEffect, useRef, useState } from 'react';

function AnimatedCounter({ target, suffix = '' }: { target: string; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const numericTarget = parseFloat(target.replace(/[^0-9.]/g, ''));
  const hasDecimal = target.includes('.');
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const duration = 2000;
          const start = Date.now();
          const tick = () => {
            const elapsed = Date.now() - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(eased * numericTarget);
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [numericTarget, hasAnimated]);

  const displayValue = hasDecimal ? count.toFixed(1) : Math.floor(count).toLocaleString();

  return (
    <span ref={ref}>
      {displayValue}{suffix}
    </span>
  );
}

function FloatingElement({ icon: Icon, className, delay = 0, color = 'text-primary', bg = 'bg-white' }: {
  icon: React.ElementType;
  className: string;
  delay?: number;
  color?: string;
  bg?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.8 + delay, type: 'spring', stiffness: 150, damping: 15 }}
      className={`absolute hidden lg:flex items-center justify-center z-10 ${className}`}
    >
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 5 + delay * 2, repeat: Infinity, ease: 'easeInOut' }}
        className={`w-12 h-12 rounded-2xl ${bg} shadow-lg border border-white/80 flex items-center justify-center backdrop-blur-sm`}
      >
        <Icon className={`w-6 h-6 ${color}`} />
      </motion.div>
    </motion.div>
  );
}

function PhoneMockup() {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useTransform(mouseY, [-300, 300], [4, -4]);
  const rotateY = useTransform(mouseX, [-300, 300], [-4, 4]);

  const tasks = [
    { title: 'Ensayo de Historia', subject: 'Historia', color: 'bg-amber-500', due: 'Hoy', priority: true },
    { title: 'Ejercicios Cap. 5', subject: 'Matemáticas', color: 'bg-blue-500', due: 'Mañana' },
    { title: 'Presentación Biología', subject: 'Biología', color: 'bg-green-500', due: 'Vie' },
    { title: 'Lectura Unidad 3', subject: 'Español', color: 'bg-purple-500', due: 'Lun' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.4, type: 'spring', stiffness: 80 }}
      className="relative flex items-center justify-center"
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        mouseX.set(e.clientX - rect.left - rect.width / 2);
        mouseY.set(e.clientY - rect.top - rect.height / 2);
      }}
      onMouseLeave={() => { mouseX.set(0); mouseY.set(0); }}
      style={{ perspective: 1200 }}
    >
      {/* Glow behind phone */}
      <div className="absolute w-[320px] h-[420px] bg-gradient-to-br from-[#a67eec]/25 via-[#5bb4f2]/20 to-[#5ddba3]/15 rounded-[3rem] blur-3xl" />

      {/* Orbiting decorative elements */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
        className="absolute w-[380px] h-[380px]"
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#a67eec]/40" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-pink-400/40" />
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-violet-400/40" />
      </motion.div>

      <motion.div
        style={{ rotateX, rotateY }}
        transition={{ type: 'spring', stiffness: 150, damping: 20 }}
      >
        {/* Phone frame */}
        <div className="relative w-[260px] h-[520px]">
          {/* Phone body - sleek design */}
          <div className="relative w-full h-full bg-gray-950 rounded-[2.8rem] p-[2.5px] shadow-2xl shadow-black/30">
            {/* Inner bezel */}
            <div className="w-full h-full bg-gray-900 rounded-[2.6rem] p-[2px]">
              {/* Screen */}
              <div className="w-full h-full rounded-[2.5rem] overflow-hidden bg-gray-50 relative">
                {/* Dynamic island / notch */}
                <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-950 rounded-full z-30" />

                {/* App header */}
                <div className="bg-gradient-to-r from-[#a67eec] to-[#8b6de0] pt-10 pb-4 px-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white text-[13px] font-bold">Mis Tareas</p>
                    <div className="flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded-full">
                      <Sparkles className="w-2.5 h-2.5 text-yellow-300" />
                      <span className="text-[8px] text-white font-medium">IA</span>
                    </div>
                  </div>
                  <p className="text-white/70 text-[10px]">3 pendientes hoy</p>
                </div>

                {/* Tasks */}
                <div className="px-3 py-2.5 space-y-1.5">
                  {tasks.map((task, i) => (
                    <motion.div
                      key={task.title}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.9 + i * 0.12, type: 'spring', stiffness: 120 }}
                      className={`flex items-center gap-2.5 p-2.5 rounded-xl border ${
                        i === 0 ? 'bg-[#a67eec]/10 border-[#a67eec]/20' : 'bg-white border-gray-100'
                      }`}
                    >
                      <div className={`w-1.5 h-9 rounded-full ${task.color} shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10.5px] font-semibold text-gray-800 truncate">{task.title}</p>
                        <p className="text-[8.5px] text-gray-400 mt-0.5">{task.subject}</p>
                      </div>
                      <span className={`text-[8px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                        task.priority ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {task.due}
                      </span>
                    </motion.div>
                  ))}
                </div>

                {/* AI card */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.6, type: 'spring' }}
                  className="mx-3 mt-1 p-3 rounded-xl bg-gradient-to-r from-[#a67eec]/10 to-[#5bb4f2]/10 border border-[#a67eec]/15"
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-4 h-4 rounded-md bg-gradient-to-br from-[#a67eec] to-[#8b6de0] flex items-center justify-center">
                      <Sparkles className="w-2.5 h-2.5 text-white" />
                    </div>
                    <span className="text-[9.5px] font-bold text-[#a67eec]">Sugerencia IA</span>
                  </div>
                  <p className="text-[8.5px] text-[#a67eec]/70 leading-relaxed">
                    Empieza por el ensayo de Historia, tiene prioridad alta y vence hoy.
                  </p>
                </motion.div>

                {/* Bottom nav */}
                <div className="absolute bottom-0 left-0 right-0 h-[52px] bg-white/95 backdrop-blur-sm border-t border-gray-100 flex items-center justify-around px-6">
                  {[
                    { label: 'Inicio', active: true },
                    { label: 'Tareas', active: false },
                    { label: 'Escanear', active: false },
                    { label: 'Notas', active: false },
                  ].map((tab) => (
                    <div key={tab.label} className={`flex flex-col items-center gap-0.5 ${tab.active ? 'text-[#a67eec]' : 'text-gray-300'}`}>
                      <div className={`w-5 h-5 rounded-lg ${tab.active ? 'bg-[#a67eec]' : 'bg-current/20'} flex items-center justify-center`}>
                        {tab.active && <div className="w-2 h-2 rounded-sm bg-white" />}
                      </div>
                      <span className={`text-[7.5px] font-semibold ${tab.active ? 'text-[#a67eec]' : 'text-gray-400'}`}>{tab.label}</span>
                    </div>
                  ))}
                </div>

                {/* Home indicator */}
                <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-24 h-1 bg-gray-900/20 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function Hero() {
  return (
    <section className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
      {/* Layered background */}
      <div className="absolute inset-0 mesh-gradient" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.02)_1.5px,transparent_1.5px),linear-gradient(90deg,rgba(99,102,241,0.02)_1.5px,transparent_1.5px)] bg-[size:50px_50px]" />

      {/* Soft blobs - using logo colors */}
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.2, 0.35, 0.2] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -top-20 -left-20 w-[600px] h-[600px] rounded-full blur-[100px]"
        style={{ background: 'linear-gradient(to bottom right, rgba(166,126,236,0.2), rgba(91,180,242,0.15))' }}
      />
      <motion.div
        animate={{ scale: [1.1, 1, 1.1], opacity: [0.15, 0.3, 0.15] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -bottom-20 -right-20 w-[700px] h-[700px] rounded-full blur-[100px]"
        style={{ background: 'linear-gradient(to bottom right, rgba(91,180,242,0.15), rgba(93,219,163,0.12))' }}
      />
      <motion.div
        animate={{ opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 7, repeat: Infinity }}
        className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full blur-[80px]"
        style={{ background: 'rgba(166,126,236,0.08)' }}
      />

      {/* Floating elements - positioned away from text area */}
      <FloatingElement icon={Camera} className="top-[12%] right-[3%]" delay={0} color="text-[#a67eec]" bg="bg-[#a67eec]/10" />
      <FloatingElement icon={Brain} className="bottom-[35%] right-[3%]" delay={0.4} color="text-[#5bb4f2]" bg="bg-[#5bb4f2]/10" />
      <FloatingElement icon={Calendar} className="bottom-[12%] left-[3%]" delay={0.8} color="text-[#5ddba3]" bg="bg-[#5ddba3]/10" />
      <FloatingElement icon={BookOpen} className="bottom-[12%] right-[15%]" delay={1.2} color="text-[#a67eec]" bg="bg-[#a67eec]/10" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-12 items-center">
          {/* Left: Content */}
          <div className="max-w-2xl mx-auto lg:mx-0 text-center lg:text-left">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/90 backdrop-blur-md border border-[#a67eec]/20 text-sm font-medium mb-8 shadow-md shadow-[#a67eec]/5">
                <motion.div animate={{ rotate: [0, 15, -15, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
                  <Sparkles size={15} className="text-amber-500" />
                </motion.div>
                <span className="text-gray-700">La app #1 para estudiantes en LATAM</span>
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100">
                  <Star size={11} className="fill-amber-400 text-amber-400" />
                  <span className="text-amber-600 font-bold text-xs">4.9</span>
                </div>
              </div>
            </motion.div>

            {/* Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl xl:text-[4.2rem] font-extrabold text-gray-900 mb-7 leading-[1.08] tracking-tight"
            >
              Tu asistente de{' '}
              <br className="hidden sm:block" />
              estudios{' '}
              <span className="relative">
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(to right, #a67eec, #5bb4f2, #5ddba3)' }}
                >
                  potenciado con IA
                </span>
                <motion.svg
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ delay: 1, duration: 0.8, ease: 'easeOut' }}
                  className="absolute -bottom-2 left-0 w-full h-3"
                  viewBox="0 0 300 12"
                  fill="none"
                  preserveAspectRatio="none"
                >
                  <motion.path
                    d="M2 8 C50 2, 100 10, 150 6 C200 2, 250 10, 298 4"
                    stroke="url(#hero-underline)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 1, duration: 0.8, ease: 'easeOut' }}
                  />
                  <defs>
                    <linearGradient id="hero-underline" x1="0" y1="0" x2="300" y2="0" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#a67eec" stopOpacity="0.5" />
                      <stop offset="50%" stopColor="#5bb4f2" stopOpacity="0.5" />
                      <stop offset="100%" stopColor="#5ddba3" stopOpacity="0.5" />
                    </linearGradient>
                  </defs>
                </motion.svg>
              </span>
            </motion.h1>

            {/* Subheading */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg md:text-xl text-gray-500 max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed"
            >
              Escanea tareas con la cámara, genera planes de estudio personalizados y
              mejora tus calificaciones.{' '}
              <span className="font-semibold text-gray-800">
                IA incluida sin costo extra.
              </span>
            </motion.p>

            {/* Pills */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-10"
            >
              {[
                { icon: Zap, text: 'IA avanzada', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200/60' },
                { icon: Shield, text: '7 días gratis', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200/60' },
                { icon: Smartphone, text: 'App + Web', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200/60' },
              ].map((item, index) => (
                <motion.div
                  key={item.text}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 + index * 0.1, type: 'spring' }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full ${item.bg} ${item.border} border shadow-sm`}
                >
                  <item.icon size={15} className={item.color} />
                  <span className="text-sm font-semibold text-gray-700">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-10"
            >
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button size="lg" className="w-full sm:w-auto group shadow-xl h-14 px-8 text-base font-semibold border-0" style={{ background: 'linear-gradient(to right, #a67eec, #8b6de0)', boxShadow: '0 10px 25px -5px rgba(166,126,236,0.3)' }}>
                  <Smartphone size={20} className="mr-2" />
                  Descargar en Google Play
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button variant="outline" size="lg" className="w-full sm:w-auto border-2 border-gray-200 h-14 px-8 text-base group bg-white/80 backdrop-blur-sm hover:bg-white hover:border-gray-300">
                  <Play size={18} className="mr-2 group-hover:scale-110 transition-transform text-[#a67eec]" />
                  Ver demo
                </Button>
              </motion.div>
            </motion.div>

            {/* Price highlight */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <div className="inline-flex flex-col sm:flex-row items-center gap-3 px-6 py-4 rounded-2xl bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-md shadow-emerald-500/5">
                <div className="flex items-center gap-2">
                  <span className="text-3xl font-extrabold bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
                    $2.99
                  </span>
                  <span className="text-gray-400">/mes</span>
                </div>
                <div className="hidden sm:block w-px h-8 bg-gray-200" />
                <div className="text-center sm:text-left">
                  <p className="text-sm text-gray-500">
                    <span className="font-semibold text-gray-800">Plan Premium</span> · Todo incluido
                  </p>
                  <p className="text-xs text-emerald-600 font-medium flex items-center gap-1 justify-center sm:justify-start">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Ahorra 30% con el plan anual
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: Phone */}
          <div className="hidden lg:flex justify-center">
            <PhoneMockup />
          </div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 max-w-4xl mx-auto"
        >
          {[
            { value: '50', suffix: 'K+', label: 'Estudiantes activos', Icon: Users, color: 'from-[#a67eec] to-[#8b6de0]', iconBg: 'bg-[#a67eec]/10', iconColor: 'text-[#a67eec]' },
            { value: '1', suffix: 'M+', label: 'Tareas completadas', Icon: CheckCircle, color: 'from-[#5bb4f2] to-[#4a9de0]', iconBg: 'bg-[#5bb4f2]/10', iconColor: 'text-[#5bb4f2]' },
            { value: '4.9', suffix: '', label: 'Rating Play Store', Icon: Star, color: 'from-[#5ddba3] to-[#4cc992]', iconBg: 'bg-[#5ddba3]/10', iconColor: 'text-[#5ddba3]' },
            { value: '6', suffix: '', label: 'Países de LATAM', Icon: MapPin, color: 'from-[#a67eec] to-[#5bb4f2]', iconBg: 'bg-[#5bb4f2]/10', iconColor: 'text-[#5bb4f2]' },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.7 + index * 0.08 }}
              whileHover={{ y: -4 }}
              className="relative p-5 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-100 text-center group cursor-default shadow-sm hover:shadow-lg hover:border-gray-200 transition-all duration-300"
            >
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${stat.iconBg} mb-3 group-hover:scale-110 transition-transform duration-300`}>
                <stat.Icon size={18} className={stat.iconColor} />
              </div>
              <p className={`text-2xl md:text-3xl font-extrabold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                <AnimatedCounter target={stat.value} suffix={stat.suffix} />
              </p>
              <p className="text-xs text-gray-400 mt-1 font-medium">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
